import { action, computed, makeObservable, observable } from 'mobx';

import { nameOf } from '../../../../app/shared/common';
import { CheckboxGroupInput as FormCheckboxGroupInput, CheckboxOption } from '../../../../app/shared/Form';
import {
  FilterTranslation,
  PriceRangeFilterTranslation,
  ProductListingFiltersTranslation,
} from '../../../localization/ModuleTranslation';
import type {
  FacetData,
  GetProductFacetsQueryResponse,
  PriceFiltersData,
  PriceRangeFacetData,
  ProductFiltersData,
} from '../../../model';
import ExpansionState from '../ExpansionState';
import NumberRangePickerState from './NumberRangePicker/NumberRangePickerState';

/** The rail's share of a product query — the search phrase is owned by the listing, not the filters. */
export type ProductFilterCriteria = Omit<ProductFiltersData, 'searchPhrase'>;

export class ProductListingFiltersState {
  @observable.ref public brands: BooleanFilter;
  @observable.ref public types: BooleanFilter;
  @observable.ref public sectors: BooleanFilter;
  @observable.ref public priceRange: PriceRangeFilter;

  public readonly expansion = new ExpansionState(() => this.filters.map((filter) => filter.id));

  public constructor(public readonly translation: ProductListingFiltersTranslation) {
    makeObservable(this);
  }

  /** The rail in render order, empty until the facets arrive; each filter is also named above. */
  @computed
  public get filters(): FilterState[] {
    return [this.sectors, this.types, this.brands, this.priceRange].filter(Boolean);
  }

  @computed
  public get criteria(): ProductFilterCriteria {
    return {
      brands: this.brands.selectedValues,
      types: this.types.selectedValues,
      sectors: this.sectors.selectedValues,
      priceFilters: this.priceRange.selectedRange,
    };
  }

  @action
  public setFilters(facets: GetProductFacetsQueryResponse, criteria: ProductFilterCriteria) {
    this.brands = new BooleanFilter(
      nameOf<GetProductFacetsQueryResponse>('brands'),
      this.translation.brands,
      facets.brands,
      criteria.brands,
    );
    this.types = new BooleanFilter(
      nameOf<GetProductFacetsQueryResponse>('types'),
      this.translation.types,
      facets.types,
      criteria.types,
    );
    this.sectors = new BooleanFilter(
      nameOf<GetProductFacetsQueryResponse>('sectors'),
      this.translation.sectors,
      facets.sectors,
      criteria.sectors,
    );
    this.priceRange = new PriceRangeFilter(
      nameOf<GetProductFacetsQueryResponse>('priceRange'),
      this.translation.priceRange,
      facets.priceRange,
      criteria.priceFilters,
    );

    this.expansion.setExpanded(this.filters.filter((filter) => filter.isActive).map((filter) => filter.id));
  }
}

export abstract class FilterState {
  public constructor(
    public readonly id: string,
    public readonly translation: FilterTranslation,
  ) {}

  /** Whether this filter is narrowing the results — which is what makes its section worth opening. */
  public abstract get isActive(): boolean;
}

export class BooleanFilter extends FilterState {
  public readonly input: FormCheckboxGroupInput;

  public constructor(id: string, translation: FilterTranslation, options: FacetData[], initialValues: string[] = []) {
    super(id, translation);

    this.input = new FormCheckboxGroupInput(
      options.map((v) => new CheckboxOption(v.id, v.text)),
      initialValues.filter((v) => options.some((o) => o.id === v)),
    );
  }

  /** Copied out of the input's observable array, so the query carries a plain snapshot. */
  public get selectedValues(): string[] {
    return [...this.input.value];
  }

  public get isActive(): boolean {
    return this.selectedValues.any();
  }
}

export class PriceRangeFilter extends FilterState {
  public readonly range: NumberRangePickerState;

  public constructor(
    id: string,
    public readonly translation: PriceRangeFilterTranslation,
    range: PriceRangeFacetData,
    initialValue: PriceRangeFacetData,
  ) {
    super(id, translation);

    this.range = new NumberRangePickerState(Math.floor(range.min), Math.ceil(range.max));

    if (initialValue?.min) {
      this.range.setMin(initialValue.min);
    }

    if (initialValue?.max) {
      this.range.setMax(initialValue.max);
    }
  }

  public get availableRange() {
    const { minValue, maxValue } = this.range;
    return { min: minValue, max: maxValue };
  }

  /** Undefined while the picker still spans the facet's own bounds: an untouched range is not a filter. */
  public get selectedRange(): PriceFiltersData | undefined {
    const { min, max, minValue, maxValue } = this.range;
    return min === minValue && max === maxValue ? undefined : { min, max };
  }

  public get isActive(): boolean {
    return !!this.selectedRange;
  }
}
